import React, { useCallback } from "react";
import { Field } from "redux-form";
import { ROLES } from "../constants";
import FormCheckbox from "./ReduxCheckbox";
import { getCascadeRuleForParent } from "../utils/ruleEngine";
import { RULES as DEFAULT_RULES } from "../config/rules.config";
import {
  computeParentClickChanges,
  computeChildClickChanges,
  computeGrandchildClickChanges,
} from "../utils/permissionEngine";

const ROLE_TABS = [
  { label: "Supervisor", value: ROLES.SUPERVISOR },
  { label: "Employee", value: ROLES.EMPLOYEE },
  { label: "Unsure", value: ROLES.UNSURE },
  { label: "System Admin", value: ROLES.ADMIN },
];

const ROLE_DESCRIPTIONS = {
  [ROLES.SUPERVISOR]:
    "You have selected the Supervisor role. To assist in assigning permissions, we have auto selected the permissions displayed below for this user. You can further customize permissions by unselecting or selecting additional one's prior to saving.",
  [ROLES.EMPLOYEE]:
    "You have selected the Employee role. To assist in assigning permissions, we have auto selected the permissions displayed below for this user. You can further customize permissions by unselecting or selecting additional one's prior to saving.",
  [ROLES.UNSURE]:
    "You have selected the Unsure role. Currently there is no permission selected, you can customize permissions by selecting permissions prior to saving.",
  [ROLES.ADMIN]:
    "You have selected the System Admin role. To assist in assigning permissions, we have auto selected the permissions displayed below for this user. You can further customize permissions by unselecting or selecting additional one's prior to saving.",
};

const PermissionSection = ({
  selectedRole,
  onRoleChange,
  filteredPermissions,
  derivedState,
  applyChanges,
  rules,
  formValues,
}) => {
  const activeRules = rules || DEFAULT_RULES;

  const renderGroup = (group) => {
    const { parentKey, label, children } = group;
    const cascadeRule = getCascadeRuleForParent(parentKey, activeRules);

    const handleParentClick = (checked) => {
      const changes = computeParentClickChanges(
        parentKey,
        checked,
        group,
        cascadeRule,
      );
      applyChanges(changes);
    };

    const handleChildClick = (checked, childKey) => {
      const childConfig = children.find((c) => c.id === childKey);
      const changes = computeChildClickChanges(
        childKey,
        checked,
        childConfig,
        cascadeRule,
        parentKey,
        formValues,
      );
      applyChanges(changes);
    };

    const handleTemplateClick = (checked, templateId, parentChild) => {
      const changes = computeGrandchildClickChanges(
        templateId,
        checked,
        parentChild,
        cascadeRule,
        parentKey,
      );
      applyChanges(changes);
    };

    const handleSubgroupParentClick = (checked, subgroupChild) => {
      const changes = computeChildClickChanges(
        subgroupChild.id,
        checked,
        subgroupChild,
        cascadeRule,
        parentKey,
        formValues,
      );
      applyChanges(changes);
    };

    const handleSubChildClick = (checked, subChildKey, subgroupChild) => {
      const changes = computeGrandchildClickChanges(
        subChildKey,
        checked,
        subgroupChild,
        cascadeRule,
        parentKey,
      );
      applyChanges(changes);
    };

    return (
      <React.Fragment key={group.key}>
        <li className="add-user__permission__heading">
          <div className="formGroup mrNone">
            <Field
              name={parentKey}
              component={FormCheckbox}
              label={label}
              onFieldValueChange={handleParentClick}
              formGroup="formGroup"
            />
          </div>
        </li>
        {children.map((child) => {
          if (child.type === "subgroup") {
            const isSubLocked = derivedState.lockedPermissions.has(child.id);
            const isSubDisabled = derivedState.disabledPermissions.has(
              child.id,
            );
            return (
              <React.Fragment key={child.id}>
                <li className="wth-12">
                  <div className="formGroup mrNone">
                    <Field
                      name={child.id}
                      component={FormCheckbox}
                      label={child.label}
                      onFieldValueChange={
                        isSubLocked || isSubDisabled
                          ? undefined
                          : (chk) => handleSubgroupParentClick(chk, child)
                      }
                      isLocked={isSubLocked}
                      isDisabled={isSubDisabled}
                      formGroup="formGroup"
                    />
                  </div>
                  {child.children && child.children.length > 0 && (
                    <ul className="add-user__permission_layout__tree">
                      {child.children.map((subChild) => {
                        const isScLocked =
                          derivedState.lockedPermissions.has(subChild.id);
                        const isScDisabled =
                          derivedState.disabledPermissions.has(subChild.id);
                        return (
                          <li key={subChild.id} className="wth-6">
                            <div className="formGroup mrNone">
                              <Field
                                name={subChild.id}
                                component={FormCheckbox}
                                label={subChild.label}
                                onFieldValueChange={
                                  isScLocked || isScDisabled
                                    ? undefined
                                    : (chk) =>
                                        handleSubChildClick(
                                          chk,
                                          subChild.id,
                                          child,
                                        )
                                }
                                isLocked={isScLocked}
                                isDisabled={isScDisabled}
                                formGroup="formGroup"
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              </React.Fragment>
            );
          }

          // Regular child
          const isChildLocked = derivedState.lockedPermissions.has(child.id);
          const isChildDisabled = derivedState.disabledPermissions.has(
            child.id,
          );
          return (
            <React.Fragment key={child.id}>
              <li className="wth-6">
                <div className="formGroup mrNone">
                  <Field
                    name={child.id}
                    component={FormCheckbox}
                    label={child.label}
                    onFieldValueChange={
                      isChildLocked || isChildDisabled
                        ? undefined
                        : (chk) => handleChildClick(chk, child.id)
                    }
                    isLocked={isChildLocked}
                    isDisabled={isChildDisabled}
                    formGroup="formGroup"
                  />
                </div>
              </li>
              {child.templates && child.templates.length > 0 && (
                <li className="wth-12">
                  <ul className="add-user__permission_layout__tree">
                    {child.templates.map((t) => {
                      const isTLocked = derivedState.lockedPermissions.has(
                        t.id,
                      );
                      const isTDisabled = derivedState.disabledPermissions.has(
                        t.id,
                      );
                      return (
                        <li key={t.id} className="wth-6">
                          <div className="formGroup mrNone">
                            <Field
                              name={t.id}
                              component={FormCheckbox}
                              label={t.name}
                              onFieldValueChange={
                                isTLocked || isTDisabled
                                  ? undefined
                                  : (chk) =>
                                      handleTemplateClick(chk, t.id, child)
                              }
                              isLocked={isTLocked}
                              isDisabled={isTDisabled}
                              formGroup="formGroup"
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              )}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  const renderSingle = (permission) => {
    const isLocked = derivedState.lockedPermissions.has(permission.id);
    const isDisabled = derivedState.disabledPermissions.has(permission.id);
    return (
      <li key={permission.key} className="add-user__permission__heading">
        <div className="formGroup mrNone">
          <Field
            name={permission.id}
            component={FormCheckbox}
            label={permission.label}
            isLocked={isLocked}
            isDisabled={isDisabled}
            formGroup="formGroup"
          />
        </div>
      </li>
    );
  };

  const renderDynamicGroup = (section) => {
    const cascadeRule = getCascadeRuleForParent(section.parentKey, activeRules);

    const handleDynParentClick = (checked) => {
      const changes = computeParentClickChanges(
        section.parentKey,
        checked,
        section,
        cascadeRule,
      );
      applyChanges(changes);
    };

    const handleDynChildClick = (checked, childId) => {
      const childConfig = (section.children || []).find(
        (c) => c.id === childId,
      );
      const changes = computeChildClickChanges(
        childId,
        checked,
        childConfig,
        cascadeRule,
        section.parentKey,
        formValues,
      );
      applyChanges(changes);
    };

    return (
      <React.Fragment key={section.key}>
        <li className="add-user__permission__heading">
          <div className="formGroup mrNone">
            <Field
              name={section.parentKey}
              component={FormCheckbox}
              label={section.label}
              onFieldValueChange={handleDynParentClick}
              formGroup="formGroup"
            />
          </div>
        </li>
        {(section.children || []).map((child) => {
          const isChildLocked = derivedState.lockedPermissions.has(child.id);
          const isChildDisabled = derivedState.disabledPermissions.has(
            child.id,
          );
          return (
            <li key={child.id} className="wth-6">
              <div className="formGroup mrNone">
                <Field
                  name={child.id}
                  component={FormCheckbox}
                  label={child.label}
                  onFieldValueChange={
                    isChildLocked || isChildDisabled
                      ? undefined
                      : (chk) => handleDynChildClick(chk, child.id)
                  }
                  isLocked={isChildLocked}
                  isDisabled={isChildDisabled}
                  formGroup="formGroup"
                />
              </div>
            </li>
          );
        })}
      </React.Fragment>
    );
  };

  const renderDynamicSectioned = (section) => {
    const cascadeRule = getCascadeRuleForParent(section.parentKey, activeRules);

    const handleSecParentClick = (checked) => {
      const changes = computeParentClickChanges(
        section.parentKey,
        checked,
        section,
        cascadeRule,
      );
      applyChanges(changes);
    };

    const handleSecChildClick = (checked, perm) => {
      const changes = computeChildClickChanges(
        perm.id,
        checked,
        perm,
        cascadeRule,
        section.parentKey,
        formValues,
      );
      applyChanges(changes);
    };

    const handleSecGrandchildClick = (checked, opt, perm) => {
      const changes = computeGrandchildClickChanges(
        opt.id,
        checked,
        perm,
        cascadeRule,
        section.parentKey,
      );
      applyChanges(changes);
    };

    return (
      <React.Fragment key={section.key}>
        <li className="add-user__permission__heading">
          <div className="formGroup mrNone">
            <Field
              name={section.parentKey}
              component={FormCheckbox}
              label={section.label}
              onFieldValueChange={handleSecParentClick}
              formGroup="formGroup"
            />
          </div>
        </li>
        {(section.sections || []).map((sub) => {
          const permissions = sub.permissions || [];
          const withOptions = permissions.filter(
            (perm) => perm.options && perm.options.length > 0,
          );
          const withoutOptions = permissions.filter(
            (perm) => !perm.options || perm.options.length === 0,
          );

          const renderPlainItem = (perm, className = "wth-12") => {
            const isPLocked = derivedState.lockedPermissions.has(perm.id);
            const isPDisabled = derivedState.disabledPermissions.has(perm.id);
            return (
              <li key={perm.id} className={className}>
                <div className="formGroup mrNone">
                  <Field
                    name={perm.id}
                    component={FormCheckbox}
                    label={perm.label}
                    isLocked={isPLocked}
                    isDisabled={isPDisabled}
                    formGroup="formGroup"
                    onFieldValueChange={
                      isPLocked || isPDisabled
                        ? undefined
                        : (chk) => handleSecChildClick(chk, perm)
                    }
                  />
                </div>
              </li>
            );
          };

          const renderItemWithOptions = (perm) => {
            const isPLocked = derivedState.lockedPermissions.has(perm.id);
            const isPDisabled = derivedState.disabledPermissions.has(perm.id);
            return (
              <li key={perm.id} className="wth-12">
                <div className="formGroup mrNone">
                  <Field
                    name={perm.id}
                    component={FormCheckbox}
                    label={perm.label}
                    isLocked={isPLocked}
                    isDisabled={isPDisabled}
                    formGroup="formGroup"
                    onFieldValueChange={
                      isPLocked || isPDisabled
                        ? undefined
                        : (chk) => handleSecChildClick(chk, perm)
                    }
                  />
                </div>
                <ul className="add-user__permission_layout__tree">
                  {perm.options.map((opt) => {
                    const isOLocked = derivedState.lockedPermissions.has(opt.id);
                    const isODisabled = derivedState.disabledPermissions.has(
                      opt.id,
                    );
                    return (
                      <li key={opt.id} className="wth-12">
                        <div className="formGroup mrNone">
                          <Field
                            name={opt.id}
                            component={FormCheckbox}
                            label={opt.label}
                            isLocked={isOLocked}
                            isDisabled={isODisabled}
                            formGroup="formGroup"
                            onFieldValueChange={
                              isOLocked || isODisabled
                                ? undefined
                                : (chk) =>
                                    handleSecGrandchildClick(chk, opt, perm)
                            }
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          };

          return (
            <React.Fragment key={sub.id}>
              <li className="wth-12">
                <div className="formGroup mrNone">
                  <strong>{sub.label}</strong>
                </div>
                {withOptions.length === 0 ? (
                  <ul className="add-user__permission_layout__tree">
                    {permissions.map((perm) => renderPlainItem(perm, "wth-6"))}
                  </ul>
                ) : (
                  <div className="rowFlex">
                    <ul className="add-user__permission_layout__tree wth-6">
                      {withOptions.map(renderItemWithOptions)}
                    </ul>
                    <ul className="add-user__permission_layout__tree wth-6">
                      {withoutOptions.map((perm) => renderPlainItem(perm))}
                    </ul>
                  </div>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <div className="add-user__permission">
      <div className="tabNav mt">
        <div className="tabs" data-test="tabs">
          {ROLE_TABS.map((tab) => (
            <a
              key={tab.value}
              href="javascript:void(0)"
              className={selectedRole === tab.value ? "active" : ""}
              onClick={() => onRoleChange(tab.value)}
            >
              {tab.label}
            </a>
          ))}
        </div>
      </div>
      <div className="add-user__permission__info">
        <p>{ROLE_DESCRIPTIONS[selectedRole] || ""}</p>
      </div>
      <div className="add-user__permission__layout">
        <ul className="wth-12">
          {filteredPermissions.map((entry) => {
            switch (entry.type) {
              case "group":
                return renderGroup(entry);
              case "single":
                return renderSingle(entry);
              case "dynamic-group":
                return renderDynamicGroup(entry);
              case "dynamic-sectioned":
                return renderDynamicSectioned(entry);
              default:
                return null;
            }
          })}
        </ul>
      </div>
    </div>
  );
};

export default PermissionSection;
